import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, BellRing, Flame, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ProfileModal({ open, onClose, user, onAuth }) {
  const [suburb, setSuburb] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [station, setStation] = useState(null);
  const [watchAreas, setWatchAreas] = useState([]);
  const [watchQuery, setWatchQuery] = useState("");
  const [watchResults, setWatchResults] = useState([]);
  const [notify, setNotify] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setSuburb(user?.profile?.suburb || "");
      setStation(
        user?.profile?.home_station_name
          ? {
              code: user.profile.home_station_code,
              name: user.profile.home_station_name,
            }
          : null
      );
      setQuery("");
      setResults([]);
      setWatchAreas(user?.profile?.watch_areas || []);
      setNotify(Boolean(user?.profile?.notify));
      setWatchQuery("");
      setWatchResults([]);
      setError("");
      window.__lenis?.stop();
      const onKey = (e) => e.key === "Escape" && onClose();
      window.addEventListener("keydown", onKey);
      return () => {
        window.__lenis?.start();
        window.removeEventListener("keydown", onKey);
      };
    }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .get("/stations", { params: { search: query, limit: 6 } })
        .then((r) => setResults(r.data.items))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!watchQuery.trim()) {
      setWatchResults([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .get("/stations", { params: { search: watchQuery, limit: 6 } })
        .then((r) =>
          setWatchResults(
            r.data.items.filter((s) => !watchAreas.some((w) => w.code === s.code))
          )
        )
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [watchQuery, watchAreas]);

  const toggleNotify = async () => {
    if (notify) {
      setNotify(false);
      return;
    }
    if (typeof Notification === "undefined") {
      toast.error("This browser doesn't support notifications");
      return;
    }
    if (Notification.permission === "granted") {
      setNotify(true);
      return;
    }
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      setNotify(true);
    } else {
      toast.error("Notifications blocked — allow them in your browser settings");
    }
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.put("/auth/profile", {
        suburb,
        home_station_code: station?.code || "",
        home_station_name: station?.name || "",
        watch_areas: watchAreas,
        notify,
      });
      onAuth(data);
      onClose();
      toast.success("Profile saved — watch the feed for your patch");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="profile-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-[80] grid place-items-center bg-black/70 p-5 backdrop-blur-sm"
        >
          <motion.div
            key="profile-card"
            data-testid="profile-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="w-full max-w-sm border border-[#27272A] bg-[#0D0D0D]"
          >
            <div className="flex items-center justify-between border-b border-[#27272A] px-6 py-4">
              <span className="flex items-center gap-2 font-jb text-[10px] tracking-[0.35em] text-zinc-500">
                <Flame className="h-3.5 w-3.5 text-[#FF3B30]" />
                MY CREW
              </span>
              <button
                data-testid="profile-close-btn"
                onClick={onClose}
                aria-label="Close profile"
                className="grid h-8 w-8 place-items-center border border-[#27272A] text-zinc-400 transition-colors duration-300 hover:border-[#FF3B30] hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-6 px-6 py-6">
              <div>
                <label className="mb-2 flex items-center gap-2 font-jb text-[10px] tracking-[0.3em] text-[#FF5247]">
                  <Bell className="h-3.5 w-3.5" />
                  ALERT SUBURB
                </label>
                <input
                  data-testid="profile-suburb-input"
                  value={suburb}
                  onChange={(e) => setSuburb(e.target.value)}
                  placeholder="E.G. WOLLERT"
                  maxLength={60}
                  className="w-full border border-[#27272A] bg-black/40 px-4 py-3 font-jb text-xs tracking-[0.15em] text-white placeholder:text-zinc-600 focus:border-[#FF3B30] focus:outline-none"
                />
                <p className="mt-2 font-jb text-[9px] leading-relaxed tracking-[0.15em] text-zinc-600">
                  JOBS IN THIS SUBURB GLOW RED IN YOUR FEED.
                </p>
              </div>

              <div>
                <label className="mb-2 flex items-center gap-2 font-jb text-[10px] tracking-[0.3em] text-[#FF6B00]">
                  <MapPin className="h-3.5 w-3.5" />
                  HOME BRIGADE / STATION
                </label>
                {station ? (
                  <div className="flex items-center justify-between border border-[#FF6B00]/40 bg-[#FF6B00]/10 px-4 py-3">
                    <span
                      data-testid="profile-station-selected"
                      className="font-jb text-[11px] tracking-[0.1em] text-[#FF6B00]"
                    >
                      {station.code} — {station.name}
                    </span>
                    <button
                      data-testid="profile-clear-station-btn"
                      onClick={() => setStation(null)}
                      aria-label="Clear home station"
                      className="text-zinc-500 transition-colors hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      data-testid="profile-station-search-input"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="SEARCH 1,200+ STATIONS…"
                      className="w-full border border-[#27272A] bg-black/40 px-4 py-3 font-jb text-xs tracking-[0.15em] text-white placeholder:text-zinc-600 focus:border-[#FF3B30] focus:outline-none"
                    />
                    {results.length > 0 && (
                      <div className="mt-1 max-h-44 overflow-y-auto border border-[#27272A] bg-black/60">
                        {results.map((s) => (
                          <button
                            key={`${s.agency}-${s.code}`}
                            data-testid={`profile-station-option-${s.code}`}
                            onClick={() => {
                              setStation({ code: s.code, name: s.name });
                              setQuery("");
                              setResults([]);
                            }}
                            className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors duration-200 hover:bg-[#FF3B30]/10"
                          >
                            <span className="font-jb text-[11px] text-zinc-200">
                              {s.name}
                            </span>
                            <span className="font-jb text-[9px] tracking-[0.2em] text-zinc-600">
                              {s.agency} · {s.code}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
                <p className="mt-2 font-jb text-[9px] leading-relaxed tracking-[0.15em] text-zinc-600">
                  JOBS NEAR YOUR STATION ARE PINNED TO THE TOP.
                </p>
              </div>

              <div>
                <label className="mb-2 flex items-center gap-2 font-jb text-[10px] tracking-[0.3em] text-[#FFC107]">
                  <MapPin className="h-3.5 w-3.5" />
                  WATCH AREAS (UP TO 10)
                </label>
                {watchAreas.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {watchAreas.map((w) => (
                      <span
                        key={w.code}
                        data-testid={`watch-chip-${w.code}`}
                        className="flex items-center gap-1.5 border border-[#FFC107]/40 bg-[#FFC107]/10 px-2.5 py-1 font-jb text-[10px] tracking-[0.1em] text-[#FFC107]"
                      >
                        {w.code} — {w.name}
                        <button
                          data-testid={`watch-remove-${w.code}`}
                          onClick={() =>
                            setWatchAreas((ws) => ws.filter((x) => x.code !== w.code))
                          }
                          aria-label={`Remove ${w.name}`}
                          className="text-zinc-500 transition-colors hover:text-white"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <input
                  data-testid="watch-area-search-input"
                  value={watchQuery}
                  onChange={(e) => setWatchQuery(e.target.value)}
                  placeholder="ADD A STATION TO WATCH…"
                  className="w-full border border-[#27272A] bg-black/40 px-4 py-3 font-jb text-xs tracking-[0.15em] text-white placeholder:text-zinc-600 focus:border-[#FF3B30] focus:outline-none"
                />
                {watchResults.length > 0 && (
                  <div className="mt-1 max-h-44 overflow-y-auto border border-[#27272A] bg-black/60">
                    {watchResults.map((s) => (
                      <button
                        key={`${s.agency}-${s.code}`}
                        data-testid={`watch-option-${s.code}`}
                        onClick={() => {
                          if (watchAreas.length < 10) {
                            setWatchAreas((ws) => [...ws, { code: s.code, name: s.name }]);
                          }
                          setWatchQuery("");
                          setWatchResults([]);
                        }}
                        className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors duration-200 hover:bg-[#FF3B30]/10"
                      >
                        <span className="font-jb text-[11px] text-zinc-200">
                          {s.name}
                        </span>
                        <span className="font-jb text-[9px] tracking-[0.2em] text-zinc-600">
                          {s.agency} · {s.code}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <p className="mt-2 font-jb text-[9px] leading-relaxed tracking-[0.15em] text-zinc-600">
                  JOBS IN WATCHED AREAS ARE PINNED WITH AN AMBER BADGE.
                </p>
              </div>

              <div>
                <label className="mb-2 flex items-center gap-2 font-jb text-[10px] tracking-[0.3em] text-[#10B981]">
                  <BellRing className="h-3.5 w-3.5" />
                  NOTIFICATIONS
                </label>
                <button
                  data-testid="notify-toggle-btn"
                  onClick={toggleNotify}
                  className={`flex w-full items-center justify-between border px-4 py-3 font-jb text-[11px] tracking-[0.2em] transition-colors duration-300 ${
                    notify
                      ? "border-[#10B981]/50 bg-[#10B981]/10 text-[#10B981]"
                      : "border-[#27272A] text-zinc-500 hover:border-zinc-500"
                  }`}
                >
                  <span>ALERT ME ON NEW JOBS IN MY AREAS</span>
                  <span className="font-bold">{notify ? "ON" : "OFF"}</span>
                </button>
                <p className="mt-2 font-jb text-[9px] leading-relaxed tracking-[0.15em] text-zinc-600">
                  BROWSER PINGS WHILE THIS TAB IS OPEN. USES YOUR ALERT SUBURB,
                  HOME STATION AND WATCH AREAS.
                </p>
              </div>

              {error && (
                <p
                  data-testid="profile-error"
                  className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2 font-jb text-[10px] tracking-[0.15em] text-[#FF5247]"
                >
                  {error}
                </p>
              )}

              <button
                data-testid="profile-save-btn"
                onClick={save}
                disabled={busy}
                className="w-full bg-[#FF3B30] py-3.5 font-jb text-xs font-bold tracking-[0.3em] text-black transition-colors duration-300 hover:bg-[#FFC107] disabled:opacity-50"
              >
                {busy ? "SAVING…" : "SAVE PROFILE"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
